import { useCallback } from 'react';
import { handleMultimodalHistory, handleMultimodalDocument, handleMultimodalDocumentToken } from '@/api/grt/ai';
import { handleGetcourseDetail } from '@/api/grt/courseCommend';
import { insertMultimodalModal, removeMultimodalModal } from '@/utils/Page/modal';
import DetailModal from '@/pages/Web/PageComponents/MultimodalLearning/DetailModal';
import ToastUtils from '@/utils/ToastUtils';
import { tryParseJSON } from '../../../utils/Page/jsonUtils';
import grtStore from '@/store/grtStore';

const MULTIMODAL_LEARNING_SKILL_ID = '1993854960897699841';

/**
 * 多模态历史记录管理 Hook
 * 负责处理历史记录的加载、解析等功能
 */
const useMultimodalHistory = () => {
    /**
     * 标准化多模态历史消息
     */
    const normalizeMultimodalHistoryMessages = useCallback((payload, session) => {
        if (!payload) {
            return [];
        }

        const sessionId = session?.session_id;

        // 1. 如果直接是数组（当前多模态历史接口就是这种结构）
        if (Array.isArray(payload)) {
            const normalized = [];
            let lastQuestion = session?.session_name || '';

            payload.forEach((msg) => {
                if (!msg) return;

                // 用户问题
                if (msg.message_type === 'question' || msg.role === 'user' || msg.content_type === 'text') {
                    const question = msg.content || msg.question || lastQuestion;
                    lastQuestion = question;

                    normalized.push({
                        ...msg,
                        status: 'complete',
                        content: question,
                        question,
                        session_id: sessionId
                    });
                    return;
                }

                // 多模态结果
                if (msg.content_type === 'multimodal_learning' || msg.handler === 'multimodal' || msg.content_type === 'multimodal') {
                    let multimodalResult =
                        msg.multimodal_learning_result || tryParseJSON(msg.content) || tryParseJSON(msg.answerContent);

                    if (!multimodalResult) {
                        multimodalResult = {
                            description: msg.description || '',
                            raw: msg.content || msg.answerContent || ''
                        };
                    }

                    normalized.push({
                        ...msg,
                        role: 'assistant',
                        status: 'complete',
                        content: '',
                        session_id: sessionId,
                        question: msg.question || lastQuestion,
                        multimodal_learning_result: multimodalResult
                    });
                    return;
                }

                // 其它普通消息
                normalized.push({
                    ...msg,
                    status: msg.status || 'complete',
                    session_id: sessionId
                });
            });

            return normalized;
        }

        // 2. 兼容旧结构
        const candidateKeys = ['messages', 'history', 'records', 'data'];
        for (const key of candidateKeys) {
            const value = payload[key];
            if (Array.isArray(value)) {
                return normalizeMultimodalHistoryMessages(value, session);
            }
        }

        // 3. 再兜底一种「question + multimodal_learning_result」的结构
        const normalized = [];
        const question = payload.question || payload.prompt || payload.ask || payload.last_question || session?.session_name;

        if (question) {
            normalized.push({
                role: 'local',
                status: 'complete',
                content: question,
                question,
                session_id: sessionId
            });
        }

        const multimodalResult = payload.multimodal_learning_result || payload.result || payload.answer || payload.response;

        if (multimodalResult) {
            normalized.push({
                role: 'ai',
                status: 'complete',
                content: '',
                session_id: sessionId,
                multimodal_learning_result: multimodalResult
            });
        }

        return normalized;
    }, []);

    /**
     * 处理多模态历史记录（带UI）
     */
    const handleMultimodalHistoryWithUI = useCallback(
        async (session, { handelSessionHistoryMessage } = {}) => {
            if (!session?.session_id || typeof handelSessionHistoryMessage !== 'function') {
                return false;
            }

            try {
                const response = await handleMultimodalHistory({ session_id: session.session_id });
                if (response?.success) {
                    const normalizedMessages = normalizeMultimodalHistoryMessages(response.data, session);
                    if (normalizedMessages.length) {
                        await handelSessionHistoryMessage(normalizedMessages, session);
                        return true;
                    }
                } else {
                    console.warn('🔥 handleMultimodalHistoryWithUI - 多模态历史记录接口响应异常:', response);
                }
            } catch (error) {
                console.error('🔥 handleMultimodalHistoryWithUI - 多模态历史记录接口异常:', error);
            }

            return false;
        },
        [normalizeMultimodalHistoryMessages]
    );

    /**
     * 判断是否为多模态历史技能
     */
    const isMultimodalHistorySkill = useCallback((skillId, isMultimodalLearningTypeFn) => {
        if (!skillId) {
            return false;
        }
        if (String(skillId) === MULTIMODAL_LEARNING_SKILL_ID) {
            return true;
        }
        if (typeof isMultimodalLearningTypeFn === 'function') {
            return isMultimodalLearningTypeFn(skillId);
        }
        return false;
    }, []);

    /**
     * 创建历史记录视频列表打开函数
     */
    const createOpenHistoryMultimodalList = useCallback(
        ({
            setMultimodalReference: setMultimodalReferenceFn
        } = {}) => {
            let savedVideoReferences = [];
            
            const openHistoryMultimodalList = (videoReferences = []) => {
                savedVideoReferences = videoReferences;
                const videoList = (videoReferences || []).map((video) => ({
                    ...video,
                    thumbnail: video.image_url || video.thumbnail || '',
                    image_url: video.image_url || video.thumbnail || ''
                }));
                
                const historyDetailDataRef = { current: null };
                const historyShowVideoListRef = { current: true };
                
                const updateModal = (newData, showVideoList = false) => {
                    historyDetailDataRef.current = newData;
                    historyShowVideoListRef.current = showVideoList;
                    
                    const currentData = newData || {
                        title: '视频列表',
                        type: 'video_list',
                        description: ''
                    };
                    
                    insertMultimodalModal(
                        <DetailModal
                            visible={true}
                            data={currentData}
                            videoList={showVideoList ? videoList : []}
                            onClose={() => {
                                removeMultimodalModal('multimodal-detail-drawer');
                                const setDetailPanelOpenFn = grtStore?.multimodalStore?.setDetailPanelOpen;
                                if (setDetailPanelOpenFn) {
                                    setDetailPanelOpenFn(false);
                                }
                                historyDetailDataRef.current = null;
                            }}
                            onVideoClick={handleHistoryVideoClick}
                            onDetailClick={handleHistoryVideoDetailClick}
                            onContinueAsk={handleHistoryContinueAsk}
                            onViewNext={handleHistoryViewNext}
                            onBackToList={handleHistoryBackToList}
                        />,
                        'multimodal-detail-drawer'
                    );
                };
                
                const handleHistoryVideoClick = async (video, index, videoListFromModal = null) => {
                    try {
                        const resourceId = video?.id;
                        if (!resourceId) {
                            ToastUtils.showToast('缺少资源ID，无法打开视频');
                            console.warn('查看视频时缺少资源ID:', video);
                            return;
                        }

                        const docRes = await handleMultimodalDocument({ id: resourceId });

                        if (!docRes?.success || !docRes.data) {
                            ToastUtils.showToast(docRes?.msg || '获取视频信息失败');
                            return;
                        }

                        const docData = docRes.data;
                        let playUrl =
                            docData.play_url ||
                            docData.video_url ||
                            docData.url ||
                            docData.playUrl ||
                            docData.videoUrl ||
                            docData.default_url ||
                            docData.defaultUrl ||
                            '';

                        const vodId = docData.vod_id;
                        const assetId = docData.asset_id || docData.assetId || docData.vod_asset_id || vodId;

                        if (assetId) {
                            try {
                                const tokenRes = await handleMultimodalDocumentToken(
                                    vodId ? { vod_id: vodId } : { asset_id: assetId }
                                );

                                if(tokenRes){
                                    playUrl = `${playUrl}?token=${tokenRes}`;
                                }
                            } catch (tokenError) {
                                console.error('获取视频 token 失败:', tokenError);
                            }
                        }

                        if (!playUrl) {
                            ToastUtils.showToast('未获取到可用的视频播放地址');
                            return;
                        }

                        const rawVideoList = Array.isArray(videoListFromModal) && videoListFromModal.length > 0 
                            ? videoListFromModal 
                            : (Array.isArray(videoList) && videoList.length > 0 ? videoList : []);
                        
                        const videoListPayload = rawVideoList.map(item => ({
                            id: item.id,
                            title: item.title,
                            description: item.description || '',
                            duration: item.duration || '',
                            thumbnail: item.image_url || item.thumbnail || '',
                            videoUrl: item.video_url || item.videoUrl || ''
                        }));
                        
                        let videoListKey = '';
                        if (typeof window !== 'undefined' && window.sessionStorage) {
                            try {
                                videoListKey = `videoList_${Date.now()}_${resourceId || ''}`;
                                const videoListStr = JSON.stringify(videoListPayload);
                                window.sessionStorage.setItem(videoListKey, videoListStr);
                                const verifyData = window.sessionStorage.getItem(videoListKey);
                                if (verifyData !== videoListStr) {
                                    console.warn('⚠️ sessionStorage 写入验证失败，将使用 URL 参数');
                                    videoListKey = '';
                                }
                            } catch (storageError) {
                                console.warn('❌ 缓存视频列表失败:', storageError);
                                videoListKey = '';
                            }
                        }

                        const videoTitle = video?.title || docData?.name || '视频播放';
                        const videoPoster = video?.thumbnail || video?.image_url || docData?.extra_obs_id || docData?.poster || '';
                        const videoDescription = video?.description || docData?.description || '';
                        
                        const queryParams = {
                            videoUrl: playUrl,
                            title: videoTitle,
                            poster: videoPoster || '',
                            description: videoDescription || ''
                        };
                        
                        if (videoListKey) {
                            queryParams.videoListKey = videoListKey;
                        }
                        queryParams.videoList = JSON.stringify(videoListPayload);
                        
                        const query = new URLSearchParams(queryParams).toString();
                        const videoPageUrl = `${window.location.origin}/agent/web/o/video/${resourceId}?${query}`;
                        window.open(videoPageUrl, '_blank', 'noopener,noreferrer');
                    } catch (error) {
                        console.error('查看视频失败:', error);
                        ToastUtils.showToast(error?.message || '打开视频失败，请稍后重试');
                    }
                };

                const handleHistoryVideoDetailClick = async (video, index) => {
                    const resourceId = video.id;
                    
                    if (!resourceId) {
                        ToastUtils.showToast('缺少资源ID，无法获取详情');
                        console.warn('视频数据缺少 resource_id:', video);
                        return;
                    }

                    const loadingVideoDetailData = {
                        title: video.title,
                        type: 'video_detail',
                        video: {
                            ...video,
                            detailInfo: null,
                            loading: true
                        },
                        videoIndex: index
                    };
                    updateModal(loadingVideoDetailData, false);

                    try {
                        const response = await handleGetcourseDetail({
                            resource_id: resourceId,
                            state: 1
                        });
                        if (response?.success && response?.data) {
                            const apiData = response.data;
                            
                            let mindMapData = null;
                            if (apiData.mind_map_summary_list && apiData.mind_map_summary_list.length > 0) {
                                const firstItem = apiData.mind_map_summary_list[0];
                                if (typeof firstItem === 'object' && firstItem !== null && firstItem.title) {
                                    mindMapData = firstItem;
                                } else if (typeof firstItem === 'string') {
                                    mindMapData = firstItem;
                                }
                            }

                            const videoWithDetail = {
                                ...video,
                                detailInfo: {
                                    keywords: apiData.keywords || [],
                                    summary: apiData.paragraph_summary || '',
                                    mindMap: mindMapData,
                                    mindMapList: apiData.mind_map_summary_list || [],
                                    resource_name: apiData.resource_name,
                                    template_names: apiData.template_names || []
                                },
                                loading: false
                            };

                            const videoDetailData = {
                                title: apiData.resource_name || video.title,
                                type: 'video_detail',
                                video: videoWithDetail,
                                videoIndex: index
                            };

                            updateModal(videoDetailData, false);
                        } else {
                            ToastUtils.showToast(response?.msg || '获取视频详情失败');
                            const fallbackVideoDetailData = {
                                title: video.title,
                                type: 'video_detail',
                                video: {
                                    ...video,
                                    detailInfo: null,
                                    loading: false
                                },
                                videoIndex: index
                            };
                            updateModal(fallbackVideoDetailData, false);
                        }
                    } catch (error) {
                        console.error('获取视频详情失败:', error);
                        ToastUtils.showToast(error?.message || '获取视频详情失败');
                        const fallbackVideoDetailData = {
                            title: video.title,
                            type: 'video_detail',
                            video: {
                                ...video,
                                detailInfo: null,
                                loading: false
                            },
                            videoIndex: index
                        };
                        updateModal(fallbackVideoDetailData, false);
                    }
                };

                const handleHistoryContinueAsk = () => {
                    const firstVideoTitle = videoList[0]?.title || '';
                    const videoCount = videoList.length;

                    if (setMultimodalReferenceFn) {
                        const referenceData = {
                            title: '多模态学习',
                            description: '根据你的问题，我为你寻找到相关视频，并基于这些视频生成多模态学习的内容',
                            videoCount,
                            firstVideoTitle,
                            reference_id: null,
                            onListClick: () => openHistoryMultimodalList(savedVideoReferences)
                        };
                        setMultimodalReferenceFn(referenceData);
                    }
                };

                const handleHistoryViewNext = async (currentData) => {
                    if (videoList.length === 0) {
                        console.warn('没有视频数据');
                        return;
                    }

                    const currentIndex = currentData?.videoIndex;
                    if (currentIndex === undefined) {
                        console.warn('没有视频索引信息');
                        return;
                    }

                    const nextIndex = (currentIndex + 1) % videoList.length;
                    const nextVideo = videoList[nextIndex];

                    await handleHistoryVideoDetailClick(nextVideo, nextIndex);
                };

                const handleHistoryBackToList = () => {
                    const videoListData = {
                        title: '视频列表',
                        type: 'video_list',
                        description: ''
                    };
                    updateModal(videoListData, true);
                };
                
                const videoListData = {
                    title: '视频列表',
                    type: 'video_list',
                    description: ''
                };
                
                const multimodalStore = grtStore?.multimodalStore;
                const setDetailPanelOpenFn = multimodalStore?.setDetailPanelOpen;
                const setActiveDetailMessageIdFn = multimodalStore?.setActiveMessageId;

                if (setDetailPanelOpenFn) {
                    setDetailPanelOpenFn(true);
                }
                if (setActiveDetailMessageIdFn) {
                    setActiveDetailMessageIdFn(null);
                }
                
                updateModal(videoListData, true);
            };
            
            return openHistoryMultimodalList;
        },
        []
    );

    return {
        normalizeMultimodalHistoryMessages,
        handleMultimodalHistoryWithUI,
        isMultimodalHistorySkill,
        createOpenHistoryMultimodalList
    };
};

export default useMultimodalHistory;

